import React, { useState } from 'react';
import {
  Button,
  Card,
  CardFooter,
  CondensedHeader,
  CondensedLarge,
  ContextMenu,
  ContextMenuItem,
  Suspense,
  TaxonomyUtil,
  ClipboardUtil,
  ToastUtil,
} from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';
import type {
  Account,
  CandidateAccount,
  FullCandidateAccount,
  Taxonomy,
  User,
} from '@axiom/validation';

import { LinkUtil } from '../../utils/link-util';
import { EnvUtil } from '../../utils/env-util';
import { CandidateBios } from '../CandidateBios/CandidateBios';
import { AccountLegacyApi } from '../../api/account-legacy-api';

import { MyBenchCardHeader } from './MyBenchCardHeader';
import type { EditModalModeType } from './MyBenchCardSection';
import { MyBenchCardSection } from './MyBenchCardSection';
import { MyBenchCardHighlightsModal } from './MyBenchCardHighlightsModal';
import { MyBenchCardHideShowConfirmModal } from './MyBenchCardHideShowConfirmModal';
import { MyBenchCardEngageModal } from './MyBenchCardEngageModal';

export type MyBenchCardProps = {
  name?: string;
  candidate: FullCandidateAccount;
  user: User;
  isEnvoyUser?: boolean;
  account: Account;
  taxonomy: Taxonomy;
};

export const MyBenchCard = ({
  name,
  candidate,
  user,
  isEnvoyUser,
  account,
  taxonomy,
}: MyBenchCardProps) => {
  const [showHideCandidateConfirmation, setShowHideCandidateConfirmation] =
    useState(false);
  const [showEditHighlightsModal, setShowEditHighlightsModal] = useState(false);
  const [showEditEngageModal, setShowEditEngageModal] =
    useState<EditModalModeType>(null);
  const [downloadPdf, setDownloadPdf] = useState(false);

  const isEngaged =
    candidate.accountCandidateStatus ===
    CandidateOpportunitiesConst.CandidateStatuses.Engaged;
  const candidateBioUrl = LinkUtil.getShareUrl(candidate, taxonomy);

  const handleUpdateCandidate = async (body: Partial<CandidateAccount>) => {
    await AccountLegacyApi.updateAccountsCandidates(
      account.id,
      candidate.id,
      body
    );
    await AccountLegacyApi.refreshAccountCandidates(account.id);
  };

  const handleCopyLinkClick = async () => {
    await ClipboardUtil.copy({
      text: `${EnvUtil.clientUrl}${candidateBioUrl}`,
    });
    ToastUtil.add({
      name: 'copy-profile-link-to-share',
      type: 'info',
      children: <CondensedHeader>Link copied!</CondensedHeader>,
    });
  };

  const handleDownloadClick = () => {
    setDownloadPdf(true);
    ToastUtil.add({
      name: 'download-bio',
      type: 'info',
      children: (
        <CondensedLarge>
          Your PDF is being created - it will download automatically when ready.
        </CondensedLarge>
      ),
    });
  };

  return (
    <>
      <Card name={name} stretched>
        <MyBenchCardHeader
          candidate={candidate}
          taxonomy={taxonomy}
          isEnvoyUser={isEnvoyUser}
          onClickHide={() => {
            setShowHideCandidateConfirmation(true);
          }}
          onClickEditHighlights={() => {
            setShowEditHighlightsModal(true);
          }}
        />

        <MyBenchCardSection
          candidate={candidate}
          isEngaged={isEngaged}
          practiceAreaText={
            TaxonomyUtil.formatPracticeAreaFocusAreaForDisplay(
              taxonomy,
              candidate.primaryPracticeArea,
              candidate.primaryFocusArea
            ) ?? 'Not Available'
          }
          isEnvoyUser={isEnvoyUser}
          onClickShowTalent={() => {
            setShowHideCandidateConfirmation(true);
          }}
          onClickEditEngage={(type: EditModalModeType) => {
            setShowEditEngageModal(type);
          }}
          taxonomy={taxonomy}
        />

        <CardFooter stackableOn={['mobile', 'tablet']}>
          <Button
            name="VIEW_MORE_BUTTON"
            pattern="primary"
            variation="outline"
            to={candidateBioUrl}
          >
            View Profile
          </Button>
          {candidate.isSpotlightedLawyer ? (
            <ContextMenu
              direction="left"
              name="SHARE_TALENT_MENU"
              fluid
              anchor={
                <Button name="SHARE_TALENT_BUTTON" pattern="primary" fluid>
                  Share
                </Button>
              }
            >
              <ContextMenuItem
                name="COPY_LINK"
                onClick={() => {
                  void handleCopyLinkClick();
                }}
              >
                Copy Link
              </ContextMenuItem>
              <ContextMenuItem
                name="BIO"
                onClick={() => {
                  handleDownloadClick();
                }}
              >
                Download Profile
              </ContextMenuItem>
            </ContextMenu>
          ) : null}
        </CardFooter>
      </Card>
      {showHideCandidateConfirmation ? (
        <MyBenchCardHideShowConfirmModal
          candidate={candidate}
          onClose={() => {
            setShowHideCandidateConfirmation(false);
          }}
          onSubmit={(formData: Partial<CandidateAccount>) => {
            handleUpdateCandidate(formData).catch(() => {
              // Error handling: silent fail as UI already provides feedback
            });
            setShowHideCandidateConfirmation(false);
          }}
        />
      ) : null}
      {showEditHighlightsModal ? (
        <MyBenchCardHighlightsModal
          candidate={candidate}
          onClose={() => {
            setShowEditHighlightsModal(false);
          }}
          onSubmit={(formData: Partial<CandidateAccount>) => {
            handleUpdateCandidate(formData).catch(() => {
              // Error handling: silent fail as UI already provides feedback
            });
            setShowEditHighlightsModal(false);
          }}
        />
      ) : null}
      {showEditEngageModal ? (
        <MyBenchCardEngageModal
          account={account}
          user={user}
          candidate={candidate}
          type={showEditEngageModal}
          onClose={() => {
            setShowEditEngageModal(null);
          }}
        />
      ) : null}
      {downloadPdf ? (
        <Suspense fallback={null}>
          <div className="k-pdf-export" data-test="BIO_WRAPPER">
            <CandidateBios
              name="CANDIDATE_BIOS"
              onSave={() => {
                setDownloadPdf(false);
              }}
              isDownloading
              candidateId={candidate.id}
            />
          </div>
        </Suspense>
      ) : null}
    </>
  );
};
